import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { quoteRequestService } from '../../../services/quoteRequestService';
import QuoteRequestForm from '../../../components/quote/QuoteRequestForm';
import { useAuth } from '../../../contexts/AuthContext';

const PropertyCheckoutModal = ({ isOpen, onClose, property, onComplete }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    moveInDate: '',
    leaseDuration: '12',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showQuoteRequest, setShowQuoteRequest] = useState(false);
  const [quoteRequestData, setQuoteRequestData] = useState(null);

  const calculateCosts = () => {
    const months = parseInt(formData?.leaseDuration) || 1;
    const monthlyRent = property?.price || 0;
    const securityDeposit = monthlyRent * 2;
    const firstMonth = monthlyRent;
    const total = securityDeposit + firstMonth;

    return { months, monthlyRent, securityDeposit, firstMonth, total };
  };

  const costs = calculateCosts();

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.customerName || !formData?.customerEmail || !formData?.customerPhone) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!formData?.moveInDate) {
      setError('Veuillez sélectionner la date d\'emménagement');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Create quote request
      const quoteRequest = {
        requestType: 'property_rental',
        referenceId: property?.id,
        customerName: formData?.customerName,
        customerEmail: formData?.customerEmail,
        customerPhone: formData?.customerPhone,
        requestDetails: {
          propertyId: property?.id,
          propertyTitle: property?.title,
          propertyType: property?.propertyType,
          location: property?.location,
          moveInDate: formData?.moveInDate,
          leaseDuration: formData?.leaseDuration,
          monthlyRent: costs?.monthlyRent,
          notes: formData?.notes
        },
        subtotal: costs?.total,
        totalAmount: costs?.total,
        currency: 'CDF'
      };

      const createdQuoteRequest = await quoteRequestService?.createQuoteRequest(quoteRequest);

      setQuoteRequestData({
        quoteNumber: createdQuoteRequest?.quoteNumber,
        serviceType: 'Location Immobilière',
        itemName: `${property?.title} - ${property?.location}`,
        duration: `Bail de ${formData?.leaseDuration} mois`,
        totalAmount: `${costs?.total?.toLocaleString()} CDF`
      });

      setShowQuoteRequest(true);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteRequestSuccess = (result) => {
    setSuccess(true);
    setTimeout(() => {
      onComplete?.();
    }, 2000);
  };

  const handleQuoteRequestError = (error) => {
    setError(error?.message || 'Erreur lors de l\'envoi de la demande');
    setShowQuoteRequest(false);
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle2" size={40} className="text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Réservation Confirmée!</h2>
          <p className="text-slate-400 mb-6">
            Votre demande de location a été envoyée avec succès. Nous vous contacterons bientôt.
          </p>
          <Button variant="default" fullWidth onClick={onComplete}>
            Fermer
          </Button>
        </div>
      </div>
    );
  }

  if (showQuoteRequest && quoteRequestData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
        <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-2xl my-8">
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div>
              <h2 className="text-2xl font-bold text-white">Confirmation de Demande</h2>
              <p className="text-sm text-slate-400 mt-1">
                Référence : {quoteRequestData?.quoteNumber}
              </p>
            </div>
            <button
              onClick={() => setShowQuoteRequest(false)}
              className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          <div className="p-6">
            <QuoteRequestForm
              requestData={quoteRequestData}
              onSuccess={handleQuoteRequestSuccess}
              onError={handleQuoteRequestError}
              confirmButtonText="Confirmer la Demande"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-4xl my-8">
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-bold text-white">Réserver cette Propriété</h2>
            <p className="text-sm text-slate-400 mt-1">
              Remplissez vos informations pour confirmer la location
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Détails de la Propriété</h3>

                <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-4">
                    <Image
                      src={property?.image}
                      alt={property?.imageAlt}
                      className="w-20 h-14 rounded object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-white">{property?.title}</h4>
                      <p className="text-sm text-slate-400">
                        {property?.location}
                      </p>
                      <p className="text-sm font-medium text-blue-400 mt-1">
                        {property?.price?.toLocaleString()} CDF / mois
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <Input
                    type="date"
                    label="Date d'emménagement"
                    required
                    value={formData?.moveInDate}
                    onChange={(e) => setFormData({ ...formData, moveInDate: e?.target?.value })}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                  />
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Durée du bail (mois)
                    </label>
                    <select
                      value={formData?.leaseDuration}
                      onChange={(e) => setFormData({ ...formData, leaseDuration: e?.target?.value })}
                      className="w-full px-4 py-2 border border-white/10 rounded-lg bg-slate-900/60 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="6">6 mois</option>
                      <option value="12">12 mois</option>
                      <option value="24">24 mois</option>
                      <option value="36">36 mois</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Vos Informations</h3>
                <div className="space-y-4">
                  <Input
                    type="text"
                    label="Nom complet"
                    required
                    placeholder="Votre nom"
                    value={formData?.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e?.target?.value })}
                  />
                  <Input
                    type="email"
                    label="Email"
                    required
                    placeholder="votre@email.com"
                    value={formData?.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e?.target?.value })}
                  />
                  <Input
                    type="tel"
                    label="Téléphone"
                    required
                    placeholder=""
                    value={formData?.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e?.target?.value })}
                  />
                  <Input
                    type="text"
                    label="Notes (optionnel)"
                    placeholder="Informations supplémentaires"
                    value={formData?.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  />
                </div>
              </div>

              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Résumé des Coûts</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Premier mois de loyer</span>
                    <span className="font-medium text-white">{costs?.firstMonth?.toLocaleString()} CDF</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Dépôt de garantie (2 mois)</span>
                    <span className="font-medium text-white">{costs?.securityDeposit?.toLocaleString()} CDF</span>
                  </div>
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-white">Total à payer</span>
                      <span className="text-xl font-bold text-blue-400">{costs?.total?.toLocaleString()} CDF</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 text-right">
                      ≈ ${(costs?.total / 2500)?.toFixed(2)} USD
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-6">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3 mt-6 pt-6 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={loading || !costs?.total}
              iconName={loading ? "Loader2" : "FileText"}
              iconClassName={loading ? "animate-spin" : ""}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Traitement...' : 'Demander un Devis Gratuit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PropertyCheckoutModal;
